/**
 * Vibe cards — a small picture of what the recipient's puzzle will feel like, in
 * that theme's real colours (taken from THEMES in App.jsx). Drawn rather than
 * screenshotted so they stay sharp and weigh nothing.
 */
import { useTranslation } from "react-i18next";
import { SERIF, FONT } from "../chassis/tokens";

const MONO = "'Space Mono', ui-monospace, Menlo, monospace";

const box = (background, extra = {}) => ({
  display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6,
  height: 92, borderRadius: 15, overflow: "hidden", position: "relative", background, ...extra,
});

function WaxSeal({ size = 38 }) {
  return (
    <span style={{ width: size, height: size, borderRadius: 999, background: "radial-gradient(circle at 35% 30%, #D8406E 0%, #A11E4E 75%)", boxShadow: "0 6px 12px -4px rgba(161,30,78,0.6), inset 0 -2px 0 rgba(0,0,0,0.15)", display: "grid", placeItems: "center" }}>
      <svg width={size * 0.45} height={size * 0.45} viewBox="0 0 24 24" fill="#FFFFFF" aria-hidden="true">
        <path d="M12 20.5s-7.5-4.6-7.5-10.2A4.3 4.3 0 0 1 12 7.6a4.3 4.3 0 0 1 7.5 2.7c0 5.6-7.5 10.2-7.5 10.2z" />
      </svg>
    </span>
  );
}

const Dot = ({ top, left, right, bottom, size = 6, color, radius = 999, rotate = 0 }) => (
  <span style={{ position: "absolute", top, left, right, bottom, width: size, height: size, borderRadius: radius, background: color, transform: `rotate(${rotate}deg)` }} />
);

export const THEME_ART = {
  romantic: () => (
    <span style={box("linear-gradient(160deg, #FFF3F4 0%, #F9DCE3 100%)")}>
      <WaxSeal />
      <span style={{ fontFamily: SERIF, fontStyle: "italic", fontSize: 14, color: "#8F2A50" }}>for you, my love</span>
    </span>
  ),
  funny: () => (
    <span style={box("#FFE55C", { justifyContent: "center" })}>
      <svg width="84" height="70" viewBox="0 0 100 84" aria-hidden="true">
        <polygon points="50,2 58,20 76,8 72,28 94,26 80,42 96,56 74,58 80,80 60,68 50,84 40,68 20,80 26,58 4,56 20,42 6,26 28,28 24,8 42,20" fill="#FF2E6E" />
        <text x="50" y="50" textAnchor="middle" fontFamily="Plus Jakarta Sans, sans-serif" fontSize="19" fontWeight="800" fill="#FFFFFF" transform="rotate(-8 50 44)">POW!</text>
      </svg>
    </span>
  ),
  gaming: () => (
    <span style={box("#0A0A1F", {
      backgroundImage: "linear-gradient(rgba(120,110,255,0.10) 1px, transparent 1px), linear-gradient(90deg, rgba(120,110,255,0.10) 1px, transparent 1px)",
      backgroundSize: "12px 12px",
    })}>
      <span style={{ fontFamily: MONO, fontSize: 15, fontWeight: 700, letterSpacing: "0.06em", color: "#FF2EB8", textShadow: "0 0 10px rgba(255,46,184,0.8)" }}>PLAYER 2</span>
      <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: "0.12em", color: "#B7B2E8" }}>PRESS START</span>
    </span>
  ),
  birthday: () => (
    <span style={box("linear-gradient(160deg, #FFF3F7 0%, #FADDE8 100%)")}>
      <Dot top={14} left={18} size={7} color="#FFC53D" radius={2} rotate={20} />
      <Dot top={22} right={22} size={6} color="#6FC7E8" />
      <Dot bottom={18} left={26} size={6} color="#9B7BEA" />
      <Dot bottom={24} right={18} size={8} color="#FF5B94" radius={2} rotate={-30} />
      <svg width="52" height="54" viewBox="0 0 52 54" aria-hidden="true">
        <rect x="24" y="4" width="4" height="12" rx="2" fill="#FFB547" />
        <path d="M26 0c2 3 3 5 0 7-3-2-2-4 0-7z" fill="#FF7A2E" />
        <rect x="6" y="18" width="40" height="14" rx="5" fill="#FF85B3" />
        <rect x="2" y="30" width="48" height="20" rx="6" fill="#FF5B94" />
        <path d="M2 36c6 4 10-4 16 0s10-4 16 0 10-4 16 0" stroke="#FFFFFF" strokeWidth="2.4" fill="none" strokeLinecap="round" />
      </svg>
    </span>
  ),
  friendship: () => (
    <span style={box("linear-gradient(160deg, #FCF6E9 0%, #F2E6C9 100%)")}>
      <svg width="62" height="36" viewBox="0 0 62 36" aria-hidden="true">
        <circle cx="22" cy="18" r="15" fill="#7AA888" opacity="0.9" />
        <circle cx="40" cy="18" r="15" fill="#5C8C6B" opacity="0.85" />
      </svg>
      <span style={{ fontFamily: SERIF, fontStyle: "italic", fontSize: 14, color: "#2C3E34" }}>you + me</span>
    </span>
  ),
  spooky: () => (
    <span style={box("radial-gradient(circle at 70% 20%, #2E1D45 0%, #1A1025 70%)", { flexDirection: "row", gap: 10 })}>
      <Dot top={16} left={20} size={3} color="#F4E8FF" />
      <Dot top={60} left={34} size={2} color="rgba(244,232,255,0.7)" />
      <Dot top={26} right={26} size={2} color="#F4E8FF" />
      <svg width="34" height="34" viewBox="0 0 34 34" aria-hidden="true">
        <path d="M24 4a14 14 0 1 0 6 22A12 12 0 0 1 24 4z" fill="#CDA8E4" />
      </svg>
      <span style={{ fontFamily: SERIF, fontStyle: "italic", fontSize: 18, color: "#F4E8FF" }}>boo.</span>
    </span>
  ),
  travel: () => (
    <span style={box("linear-gradient(160deg, #FBF3E2 0%, #EAD9B8 100%)")}>
      <span style={{ display: "flex", alignItems: "center", gap: 8, background: "#FFFFFF", borderRadius: 8, padding: "7px 12px", boxShadow: "0 6px 14px -8px rgba(28,46,82,0.5)" }}>
        <span style={{ fontFamily: MONO, fontSize: 13, fontWeight: 700, color: "#1C2E52", letterSpacing: "0.04em" }}>MXP</span>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="#34508A" aria-hidden="true"><path d="M21 16v-2l-8-5V3.5a1.5 1.5 0 0 0-3 0V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5z" /></svg>
        <span style={{ fontFamily: MONO, fontSize: 13, fontWeight: 700, color: "#1C2E52", letterSpacing: "0.04em" }}>LIS</span>
      </span>
      <span style={{ fontFamily: FONT, fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", color: "#5C6B8A" }}>BOARDING PASS</span>
    </span>
  ),
  // Seasonal proposal "Natale": three baubles on gold threads over a deep-green glow, snow.
  christmas: function ChristmasArt() {
    const { t } = useTranslation();
    return (
      <span style={box("radial-gradient(circle at 50% 30%, #2A6247 0%, #163B2A 75%)", { gap: 4 })}>
        <Dot top={12} left={16} size={4} color="#F6E7C8" />
        <Dot top={34} left={30} size={3} color="#F6E7C8" />
        <Dot top={14} right={22} size={4} color="#F6E7C8" />
        <Dot top={44} right={14} size={3} color="#F6E7C8" />
        <Dot bottom={14} left={12} size={3} color="#F6E7C8" />
        <Dot bottom={10} right={30} size={3} color="#F6E7C8" />
        <svg width="68" height="54" viewBox="86 0 128 102" aria-hidden="true">
          <line x1="110" y1="0" x2="110" y2="40" stroke="#C9A14A" strokeWidth="1.5" />
          <line x1="150" y1="0" x2="150" y2="58" stroke="#C9A14A" strokeWidth="1.5" />
          <line x1="190" y1="0" x2="190" y2="34" stroke="#C9A14A" strokeWidth="1.5" />
          <rect x="105" y="38" width="10" height="6" rx="1.5" fill="#C9A14A" />
          <circle cx="110" cy="58" r="16" fill="#B3263A" />
          <path d="M98 54c8 5 16 5 24 0" stroke="#F6E7C8" strokeWidth="2" fill="none" />
          <rect x="145" y="56" width="10" height="6" rx="1.5" fill="#C9A14A" />
          <circle cx="150" cy="80" r="21" fill="#C9A14A" />
          <path d="M134 76c10 6 22 6 32 0M134 86c10 6 22 6 32 0" stroke="#163B2A" strokeWidth="2" fill="none" />
          <rect x="185" y="32" width="10" height="6" rx="1.5" fill="#C9A14A" />
          <circle cx="190" cy="52" r="15" fill="#F6E7C8" />
          <circle cx="190" cy="52" r="5" fill="#B3263A" />
        </svg>
        <span style={{ fontFamily: SERIF, fontStyle: "italic", fontWeight: 600, fontSize: 14, color: "#F6E7C8" }}>{t("app.themes.christmas.cardLine")}</span>
      </span>
    );
  },
};

export default function ThemeArt({ theme }) {
  const Art = THEME_ART[theme];
  return Art ? <Art /> : <span style={box("#F5EEF0")} />;
}
